import { useEffect, useRef } from "react";
import {
    motion,
    useMotionValue,
    useScroll,
    useSpring,
    useTransform,
} from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { T } from "@/data/content";
import { scrollToId } from "@/lib/scroll";
import { FadeUp, MaskLine } from "@/components/Reveal";
import { Play, ArrowDown } from "lucide-react";

export default function Hero() {
    const { lang } = useLang();
    const ref = useRef(null);
    const vidRef = useRef(null);

    useEffect(() => {
        const v = vidRef.current;
        if (!v) return;
        v.muted = true;
        const p = v.play();
        if (p) p.catch(() => {});
    }, []);

    const { scrollYProgress } = useScroll();
    const bgY = useTransform(scrollYProgress, [0, 0.28], ["0%", "14%"]);
    const fade = useTransform(scrollYProgress, [0, 0.22], [1, 0]);

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 60, damping: 20 });
    const sy = useSpring(my, { stiffness: 60, damping: 20 });
    const tX = useTransform(sx, (v) => v * 14);
    const tY = useTransform(sy, (v) => v * 10);

    const onMouseMove = (e) => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        mx.set((e.clientX - r.width / 2) / r.width - 0.5);
        my.set((e.clientY - r.height / 2) / r.height - 0.5);
    };

    return (
        <section
            id="top"
            ref={ref}
            data-testid="hero-section"
            onMouseMove={onMouseMove}
            className="relative flex h-[100svh] min-h-[640px] flex-col justify-end overflow-hidden bg-ink"
        >
            {/* background film loop */}
            <motion.div style={{ y: bgY }} className="absolute inset-0">
                <motion.div
                    initial={{ scale: 1.18 }}
                    animate={{ scale: 1.02 }}
                    transition={{ duration: 2.6, ease: [0.16, 1, 0.3, 1] }}
                    className="h-full w-full"
                >
                    <video
                        ref={vidRef}
                        data-testid="hero-reel-video"
                        className="h-full w-full object-cover object-[72%_40%] md:object-center"
                        autoPlay
                        muted
                        loop
                        playsInline
                        poster="/img/hero.jpg"
                    >
                        <source src="/videos/reel.mp4" type="video/mp4" />
                        <source src="/videos/reel.webm" type="video/webm" />
                    </video>
                </motion.div>
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-ink/65" />
            <div
                className="pointer-events-none absolute inset-0 opacity-40"
                style={{
                    background:
                        "radial-gradient(60% 55% at 78% 30%, rgba(138,43,226,0.22) 0%, transparent 70%)",
                }}
            />

            {/* content */}
            <motion.div
                style={{ opacity: fade }}
                className="relative z-10 mx-auto w-full max-w-[1600px] px-5 pb-28 md:px-10 md:pb-24"
            >
                <FadeUp delay={1.15} y={16}>
                    <div className="mb-6 flex items-center gap-4 font-mono text-[10px] tracking-[0.4em] text-cream/60 md:mb-8 md:text-[11px]">
                        <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-emerald" />
                        {tx(lang, T.hero.available)}
                    </div>
                </FadeUp>
                {/* davido live pill */}
                <FadeUp delay={1.5} y={12}>
                    <button
                        data-testid="hero-davido-pill"
                        onClick={() => scrollToId("#davido-block", -90)}
                        className="group mb-6 flex items-center gap-3 border border-gold/40 bg-ink/40 px-4 py-2 backdrop-blur-sm transition-all duration-500 hover:border-gold hover:bg-gold/10"
                    >
                        <img
                            src="/videos/davido-reel-2.jpg"
                            alt=""
                            className="h-7 w-7 rounded-full object-cover ring-1 ring-gold/60"
                        />
                        <span className="font-mono text-[9px] tracking-[0.3em] text-gold md:text-[10px]">
                            {tx(lang, T.hero.davido)}
                        </span>
                    </button>
                </FadeUp>

                <motion.h1
                    style={{ x: tX, y: tY }}
                    className="select-none"
                    data-testid="hero-title"
                >
                    <MaskLine delay={1.05}>
                        <span className="block font-display text-[clamp(3.2rem,11vw,10.5rem)] font-extrabold uppercase leading-[0.88] tracking-[-0.02em] text-cream">
                            GLOOM ENT.
                        </span>
                    </MaskLine>
                </motion.h1>

                <div className="mt-8 flex flex-col gap-8 md:mt-12 md:flex-row md:items-end md:justify-between">
                    <FadeUp delay={1.45} y={20}>
                        <p className="font-mono text-[10px] tracking-[0.35em] text-cream/70 md:text-[11px]">
                            {tx(lang, T.hero.positioning)}
                        </p>
                        <p className="mt-4 max-w-md text-sm font-light leading-relaxed text-cream/60 md:text-base">
                            {tx(lang, T.hero.sub)}
                        </p>
                    </FadeUp>

                    <FadeUp delay={1.6} y={20} className="flex flex-wrap items-center gap-4">
                        <button
                            data-testid="hero-cta-portfolio"
                            onClick={() => scrollToId("#work")}
                            className="group relative overflow-hidden rounded-full border border-cream/30 px-8 py-4 font-mono text-[11px] tracking-[0.3em] text-cream transition-colors duration-500 hover:border-cream"
                        >
                            <span className="absolute inset-0 -translate-x-full bg-cream transition-transform duration-500 ease-out group-hover:translate-x-0" />
                            <span className="relative transition-colors duration-500 group-hover:text-ink">
                                {tx(lang, T.hero.ctaWork)}
                            </span>
                        </button>
                        <button
                            data-testid="hero-cta-contact"
                            onClick={() => scrollToId("#contact")}
                            className="link-line px-2 py-4 font-mono text-[11px] tracking-[0.3em] text-cream/70 transition-colors hover:text-cream"
                        >
                            {tx(lang, T.hero.ctaContact)}
                        </button>
                        <button
                            data-testid="hero-cta-pricing"
                            onClick={() => {
                                window.__goPage?.("contact");
                                setTimeout(() => scrollToId("#estimator", -70), 1300);
                                setTimeout(() => scrollToId("#estimator", -70), 2200);
                            }}
                            className="group relative overflow-hidden rounded-full border border-gold/60 px-8 py-4 font-mono text-[11px] tracking-[0.3em] text-gold transition-colors duration-500 hover:border-gold"
                        >
                            <span className="absolute inset-0 -translate-x-full bg-gold transition-transform duration-500 ease-out group-hover:translate-x-0" />
                            <span className="relative transition-colors duration-500 group-hover:text-ink">
                                {tx(lang, T.hero.ctaPricing)}
                            </span>
                        </button>
                    </FadeUp>
                </div>
            </motion.div>

            {/* bottom meta row */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 1 }}
                className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-between px-5 pb-6 md:px-10"
            >
                <div className="flex items-center gap-3 font-mono text-[9px] tracking-[0.35em] text-cream/50 md:text-[10px]">
                    <span className="relative block h-8 w-px overflow-hidden bg-cream/20">
                        <span className="absolute inset-0 animate-scroll-hint bg-cream/80" />
                    </span>
                    {tx(lang, T.hero.scroll)}
                </div>
                <button
                    data-testid="hero-showreel-btn"
                    onClick={() => scrollToId("#music-videos")}
                    data-cursor="PLAY"
                    className="group flex items-center gap-3 font-mono text-[9px] tracking-[0.35em] text-cream/60 transition-colors hover:text-cream md:text-[10px]"
                >
                    <span className="relative flex h-10 w-10 items-center justify-center rounded-full border border-cream/30 transition-colors duration-300 group-hover:border-violet">
                        <span className="absolute inset-0 animate-pulse-ring rounded-full border border-violet/60" />
                        <Play size={11} className="ml-0.5 fill-cream text-cream" />
                    </span>
                    <span className="hidden sm:inline">{tx(lang, T.hero.reel)}</span>
                </button>
                <div className="hidden items-center gap-3 font-mono text-[9px] tracking-[0.3em] text-cream/40 md:flex md:text-[10px]">
                    46.20°N — 6.14°E
                    <ArrowDown size={10} className="text-violet" />
                </div>
            </motion.div>
        </section>
    );
}
